import type { Student, SeatingChart, ProximityConstraint, SeatingAssignment } from "../../types/models";
import { buildDistanceMatrix, type DistanceMatrix } from "./distance";

export interface SeatingValidationResult {
    ok: boolean;
    errors: string[];
    warnings: string[];
}

export interface ConstraintViolation {
    constraint: ProximityConstraint;
    studentA: string;
    studentB: string;
    actualDistance: number;
    requiredDistance: number;
    message: string;
}

/**
 * Validate proximity constraints before generation
 * Checks for conflicts and satisfiability
 */
export function validateProximityConstraints(
    students: Student[],
    chart: SeatingChart
): SeatingValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const studentIds = new Set(students.filter(s => !s.absent).map(s => s.id));

    // Check that all students in constraints exist
    for (const constraint of chart.proximityConstraints) {
        for (const studentId of constraint.studentIds) {
            if (!studentIds.has(studentId)) {
                const student = students.find(s => s.id === studentId);
                if (student?.absent) {
                    warnings.push(`Student "${student.name}" in constraint is absent`);
                } else if (!student) {
                    errors.push(`Student ID "${studentId}" in constraint not found`);
                }
            }
        }
    }

    // Check for conflicting constraints
    const constraintPairs = new Map<string, ProximityConstraint[]>();

    for (const constraint of chart.proximityConstraints) {
        // For each pair in the chain
        for (let i = 0; i < constraint.studentIds.length - 1; i++) {
            for (let j = i + 1; j < constraint.studentIds.length; j++) {
                const pair = [constraint.studentIds[i], constraint.studentIds[j]].sort().join('|');
                const existing = constraintPairs.get(pair) ?? [];
                existing.push(constraint);
                constraintPairs.set(pair, existing);
            }
        }
    }

    // Check for conflicts (same pair with conflicting within/apart)
    for (const [pair, constraints] of constraintPairs) {
        if (constraints.length > 1) {
            const hasWithin = constraints.some(c => c.type === 'within');
            const hasApart = constraints.some(c => c.type === 'apart');

            if (hasWithin && hasApart) {
                const withinConstraint = constraints.find(c => c.type === 'within')!;
                const apartConstraint = constraints.find(c => c.type === 'apart')!;

                // Check if they conflict (within radius < apart radius)
                if (withinConstraint.radius < apartConstraint.radius) {
                    const [idA, idB] = pair.split('|');
                    const nameA = students.find(s => s.id === idA)?.name ?? idA;
                    const nameB = students.find(s => s.id === idB)?.name ?? idB;
                    errors.push(
                        `Conflicting constraints: ${nameA} and ${nameB} must be within ${withinConstraint.radius}px but also apart by ${apartConstraint.radius}px`
                    );
                }
            }
        }
    }

    // Check for sufficient seats
    const presentStudents = students.filter(s => !s.absent);
    const totalSeats = chart.tables.reduce((sum, t) => sum + t.seats.length, 0);

    if (presentStudents.length > totalSeats) {
        errors.push(
            `Not enough seats: ${presentStudents.length} students but only ${totalSeats} seats available`
        );
    }

    // Check if "within" constraints have a reasonable radius
    for (const constraint of chart.proximityConstraints) {
        if (constraint.type === 'within' && constraint.radius < 50) {
            const names = constraint.studentIds
                .map(id => students.find(s => s.id === id)?.name ?? id)
                .join(', ');
            warnings.push(
                `"Within" constraint for ${names} has a very small radius (${constraint.radius}px) - students may need to be at the same seat`
            );
        }
    }

    return {
        ok: errors.length === 0,
        errors,
        warnings
    };
}

/**
 * Check if a specific assignment satisfies all proximity constraints
 */
export function checkAssignmentConstraints(
    assignment: SeatingAssignment,
    chart: SeatingChart,
    students: Student[]
): ConstraintViolation[] {
    const violations: ConstraintViolation[] = [];
    const matrix = buildDistanceMatrix(chart);

    // Build reverse lookup: studentId -> seatId
    const studentToSeat = new Map<string, string>();
    for (const [seatId, studentId] of assignment.seatAssignments) {
        if (studentId) {
            studentToSeat.set(studentId, seatId);
        }
    }

    for (const constraint of chart.proximityConstraints) {
        // Check all pairs in the constraint chain
        for (let i = 0; i < constraint.studentIds.length - 1; i++) {
            for (let j = i + 1; j < constraint.studentIds.length; j++) {
                const studentIdA = constraint.studentIds[i];
                const studentIdB = constraint.studentIds[j];

                // Skip if indices are invalid
                if (!studentIdA || !studentIdB) continue;

                const seatA = studentToSeat.get(studentIdA);
                const seatB = studentToSeat.get(studentIdB);

                // Skip if either student is not assigned (absent)
                if (!seatA || !seatB) continue;

                const distance = matrix.get(seatA, seatB);
                const nameA = students.find(s => s.id === studentIdA)?.name ?? studentIdA;
                const nameB = students.find(s => s.id === studentIdB)?.name ?? studentIdB;

                if (constraint.type === 'within') {
                    if (distance > constraint.radius) {
                        violations.push({
                            constraint,
                            studentA: studentIdA,
                            studentB: studentIdB,
                            actualDistance: distance,
                            requiredDistance: constraint.radius,
                            message: `${nameA} and ${nameB} are ${Math.round(distance)}px apart but should be within ${constraint.radius}px`
                        });
                    }
                } else if (constraint.type === 'apart') {
                    if (distance < constraint.radius && distance !== Infinity) {
                        violations.push({
                            constraint,
                            studentA: studentIdA,
                            studentB: studentIdB,
                            actualDistance: distance,
                            requiredDistance: constraint.radius,
                            message: `${nameA} and ${nameB} are only ${Math.round(distance)}px apart but should be at least ${constraint.radius}px apart`
                        });
                    }
                }
            }
        }
    }

    return violations;
}

/**
 * Score an assignment based on constraint satisfaction
 * Returns a value from 0 (all violated) to 1 (all satisfied)
 */
export function scoreConstraintSatisfaction(
    assignment: SeatingAssignment,
    chart: SeatingChart,
    students: Student[]
): number {
    if (chart.proximityConstraints.length === 0) return 1;

    const violations = checkAssignmentConstraints(assignment, chart, students);
    const totalPairs = chart.proximityConstraints.reduce((sum, c) => {
        const n = c.studentIds.length;
        return sum + (n * (n - 1)) / 2; // number of pairs in chain
    }, 0);

    if (totalPairs === 0) return 1;

    return 1 - (violations.length / totalPairs);
}

/**
 * Get all students that must be together based on "within" constraints
 * Returns groups of student IDs that should be placed together
 */
export function getWithinGroups(chart: SeatingChart): string[][] {
    const withinConstraints = chart.proximityConstraints.filter(c => c.type === 'within');

    // Use union-find to merge overlapping groups
    const parent = new Map<string, string>();

    function find(id: string): string {
        if (!parent.has(id)) {
            parent.set(id, id);
        }
        if (parent.get(id) !== id) {
            parent.set(id, find(parent.get(id)!));
        }
        return parent.get(id)!;
    }

    function union(a: string, b: string): void {
        const rootA = find(a);
        const rootB = find(b);
        if (rootA !== rootB) {
            parent.set(rootA, rootB);
        }
    }

    // Union all students in each "within" constraint
    for (const constraint of withinConstraints) {
        const first = constraint.studentIds[0];
        if (!first) continue;
        for (let i = 1; i < constraint.studentIds.length; i++) {
            const other = constraint.studentIds[i];
            if (other) union(first, other);
        }
    }

    // Group by root
    const groups = new Map<string, string[]>();
    for (const id of parent.keys()) {
        const root = find(id);
        const group = groups.get(root) ?? [];
        group.push(id);
        groups.set(root, group);
    }

    return Array.from(groups.values()).filter(g => g.length > 1);
}

/**
 * Get all pairs of students that must be kept apart
 * Returns array of [studentIdA, studentIdB, minDistance] tuples
 */
export function getApartPairs(chart: SeatingChart): [string, string, number][] {
    const pairs: [string, string, number][] = [];

    for (const constraint of chart.proximityConstraints) {
        if (constraint.type === 'apart') {
            // Expand chain to all pairs
            for (let i = 0; i < constraint.studentIds.length - 1; i++) {
                for (let j = i + 1; j < constraint.studentIds.length; j++) {
                    const idA = constraint.studentIds[i];
                    const idB = constraint.studentIds[j];
                    if (idA && idB) {
                        pairs.push([idA, idB, constraint.radius]);
                    }
                }
            }
        }
    }

    return pairs;
}
